import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, TextField } from "@mui/material";

import ReportePagina from "../../components/common/ReportePagina";
import FiltroFilial from "../../components/common/FiltroFilial";
import { useFiltroFilial } from "../../hooks/useFiltroFilial";
import { getsTerapiasActivas } from "../../services/terapiaService";

// Reporte de Terapias: que terapia tiene cada participante, con quien la lleva
// y en que filial y salon. Es el padron de la carga terapeutica.
const Terapias = () => {
    const [filtro, setFiltro] = useState("");
    const [terapias, setTerapias] = useState([]);
    const [cargando, setCargando] = useState(true);
    // El filtro de filial: el usuario con una filial solo ve esa.
    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const fetchResults = useCallback(async () => {
        setCargando(true);

        await getsTerapiasActivas(true)
            .then((data) => {
                setTerapias(data || []);
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
    }, [])

    useEffect(() => {
        fetchResults();
    }, [fetchResults])

    const filas = useMemo(() => terapias
        .map((terapia) => ({
            id: terapia.id,
            tipo: terapia.tipo || "",
            numero: terapia.numero || "",
            participante: terapia.participante || "",
            terapeuta: terapia.terapeuta || "",
            idFilial: terapia.idFilial || 0,
            fechaInicio: terapia.fechaInicio ? terapia.fechaInicio.substring(0, 10) : "",
            horario: terapia.horario || "",
            filial: terapia.filial || "",
            salon: terapia.salon || "",
            estado: terapia.estado || "",
        }))
        .filter((fila) => {
            // El filtro de filial: si se eligio una, solo sus terapias.
            if (idFilial > 0 && fila.idFilial !== idFilial) return false;

            if (filtro == "") return true;

            const texto = filtro.toLowerCase();

            return fila.participante.toLowerCase().includes(texto)
                || fila.terapeuta.toLowerCase().includes(texto)
                || (fila.numero || "").toLowerCase().includes(texto);
        })
        .sort((a, b) => (a.participante || "").localeCompare(b.participante || "")),
    [terapias, filtro, idFilial]);

    const participantes = new Set(filas.map((f) => f.participante).filter(Boolean));
    const terapeutas = new Set(filas.map((f) => f.terapeuta).filter(Boolean));
    // Las filiales que aparecen en las filas (las del reporte), no las del
    // filtro, que son las que el usuario puede elegir.
    const filialesEnFilas = new Set(filas.map((f) => f.filial).filter(Boolean));

    const columnas = [
        { campo: "tipo", titulo: "Tipo", ancho: 12 },
        { campo: "numero", titulo: "N°", ancho: 7, alin: "center" },
        { campo: "participante", titulo: "Participante", ancho: 24 },
        { campo: "terapeuta", titulo: "Terapeuta", ancho: 22 },
        { campo: "fechaInicio", titulo: "Inicio", ancho: 11, alin: "center" },
        { campo: "horario", titulo: "Horario", ancho: 11, alin: "center" },
        { campo: "filial", titulo: "Filial", ancho: 13 },
        { campo: "estado", titulo: "Estado", ancho: 10, alin: "center" },
    ];

    return (
        <ReportePagina
            titulo="Participantes por Terapia"
            subtitulo="Qué participante tiene cada terapia, con qué terapeuta, en qué filial y desde cuándo."
            cargando={cargando}
            indicadores={[
                { etiqueta: "Terapias", valor: String(filas.length), color: "#5b2d8e" },
                { etiqueta: "Participantes", valor: String(participantes.size), color: "#00796b" },
                { etiqueta: "Terapeutas", valor: String(terapeutas.size), color: "#e07b00" },
                { etiqueta: "Filiales", valor: String(filialesEnFilas.size), color: "#c8102e" },
            ]}
            filtros={
                <Grid container spacing={1}>
                    <Grid item xs={8} sm={4}>
                        <TextField
                            label="Participante, terapeuta o N°"
                            value={filtro}
                            fullWidth
                            size="small"
                            onChange={(event) => setFiltro(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            bloqueado={bloqueado}
                            onChange={setIdFilial}
                        />
                    </Grid>
                </Grid>
            }
            grafico={filas.length ? {
                titulo: "Terapias por terapeuta",
                subtitulo: "Número de terapias asignadas",
                etiquetas: Array.from(terapeutas).slice(0, 12),
                valores: Array.from(terapeutas).slice(0, 12).map((t) => filas.filter((f) => f.terapeuta === t).length),
            } : null}
            columnas={columnas}
            filas={filas}
        />
    )
}

export default Terapias;